/**
 * TechnicalDocumentation - Kompletná technická dokumentácia pre technikov
 * Obsahuje: stĺpiky, káble, zvary, passthroughs, splittre, ONU, schéma, merania
 */
import React, { useState, useEffect } from 'react';
import { useProjectStore, usePoleStore, useCableStore } from '../../stores';
import { useONUStore } from '../../stores/onuStore';
import { Pole, Cable } from '../../types';
import { getFiberColorInBuffer } from '../../utils/fiberColors';
import './TechnicalDocumentation.css';

// Helper function to get fiber color name
const getFiberColorName = (fiberNumber: number): string => {
  return getFiberColorInBuffer(fiberNumber, 24);
};

const SPLITTER_LOSS: Record<string, number> = {
  '1:2': 3.5, '1:4': 7.0, '1:8': 10.5,
  '1:16': 13.5, '1:32': 17.0, '1:64': 20.5,
};

export const TechnicalDocumentation: React.FC = () => {
  const { currentProject } = useProjectStore();
  const { poles, fetchPoles } = usePoleStore();
  const { cables, fetchCables } = useCableStore();
  const { onus, fetchONUs } = useONUStore();
  const [activeSection, setActiveSection] = useState<string>('overview');

  // Print filter state
  const [showPrintFilter, setShowPrintFilter] = useState(false);
  const [printSections, setPrintSections] = useState({
    overview: true,
    poles: true,
    cables: true,
    splices: true,
    passthroughs: true,
    splitters: true,
    customerSplitters: true,
    onus: true,
  });

  useEffect(() => {
    if (currentProject) {
      console.log('📚 TechnicalDocumentation: Loading data for project', currentProject.id);
      fetchPoles(currentProject.id);
      fetchCables(currentProject.id);
      fetchONUs(currentProject.id);
    }
  }, [currentProject, fetchPoles, fetchCables, fetchONUs]);

  useEffect(() => {
    console.log('🔄 TechnicalDocumentation: Data updated');
    console.log('  Poles:', poles.length);
    console.log('  Cables:', cables.length);
    console.log('  ONUs:', onus.length);
  }, [poles, cables, onus]);

  // Natural sort function for pole names (Stĺpik_1, Stĺpik_2, ..., Stĺpik_10, ...)
  const naturalSort = (a: Pole, b: Pole): number => {
    const nameA = a.name || '';
    const nameB = b.name || '';

    // Split into parts: text and numbers
    const partsA = nameA.match(/(\d+|\D+)/g) || [];
    const partsB = nameB.match(/(\d+|\D+)/g) || [];

    for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
      const partA = partsA[i] || '';
      const partB = partsB[i] || '';

      // If both parts are numbers, compare numerically
      const numA = parseInt(partA, 10);
      const numB = parseInt(partB, 10);

      if (!isNaN(numA) && !isNaN(numB)) {
        if (numA !== numB) return numA - numB;
      } else {
        // Otherwise compare as strings
        if (partA !== partB) return partA.localeCompare(partB);
      }
    }

    return 0;
  };

  const getPoleName = (poleId: number): string => {
    const pole = poles.find((p) => p.id === poleId);
    return pole ? pole.name : `Stĺpik #${poleId}`;
  };

  const getCableName = (cableId: number): string => {
    const cable = cables.find((c) => c.id === cableId);
    if (!cable) return `Kábel #${cableId}`;
    return `Kábel #${cableId} (${getPoleName(cable.from_pole_id)} → ${getPoleName(cable.to_pole_id)})`;
  };

  // Sort poles naturally (Stĺpik_1, Stĺpik_2, ..., Stĺpik_10, ...)
  const sortedPoles = [...poles].sort(naturalSort);
  const sortedPolesWithCassettes = sortedPoles.filter((p) => p.cassette?.has_cassette);

  // Statistics
  const totalPoles = poles.length;
  const totalCables = cables.length;
  const totalLength = cables.reduce((sum, c) => {
    const length = c.length_meters ? (typeof c.length_meters === 'number' ? c.length_meters : parseFloat(String(c.length_meters))) : 0;
    return sum + length;
  }, 0);
  const polesWithCassettes = poles.filter((p) => p.cassette?.has_cassette);
  const totalSplitters = polesWithCassettes.reduce((sum, p) => sum + (p.cassette?.splitters?.length || 0), 0);
  const totalSplices = polesWithCassettes.reduce((sum, p) => sum + (p.cassette?.splices?.length || 0), 0);
  const totalPassthroughs = polesWithCassettes.reduce((sum, p) => sum + (p.cassette?.pass_through_cables?.length || 0), 0);
  const totalONUs = onus.length;
  const activeONUs = onus.filter((o) => {
    if (!o.signal_strength) return false;
    const signal = typeof o.signal_strength === 'string' ? parseFloat(o.signal_strength) : o.signal_strength;
    return signal > -28;
  }).length;

  const handlePrint = () => {
    console.log('🖨️ PRINT BUTTON CLICKED');
    console.log('📋 Print filter settings:', printSections);

    // Get all sections
    const sections = document.querySelectorAll('.tech-doc-section');
    console.log('📄 Total sections in DOM:', sections.length);

    // BEFORE print: Show/hide sections based on filter
    const originalClasses: string[] = [];
    sections.forEach((section, idx) => {
      originalClasses[idx] = section.className;
      const sectionName = section.getAttribute('data-section');
      console.log(`  BEFORE: Section ${idx} (${sectionName}): "${section.className}"`);

      // Check if this section should be printed
      if (!sectionName) {
        console.warn(`  ⚠️ Section ${idx} has no data-section attribute!`);
        return;
      }

      const shouldPrint = printSections[sectionName as keyof typeof printSections];
      console.log(`  → Should print "${sectionName}": ${shouldPrint}`);

      if (shouldPrint) {
        // Remove 'inactive' class temporarily and show for print
        section.classList.remove('inactive');
        section.classList.add('active');
        section.classList.remove('print-hidden');
      } else {
        // Hide this section for print
        section.classList.add('print-hidden');
      }
      console.log(`  AFTER:  Section ${idx} (${sectionName}): "${section.className}"`);
    });

    // Wait for browser to re-render before opening print dialog
    console.log('⏳ Waiting 200ms for browser to re-render...');
    setTimeout(() => {
      // NOW log table info (after sections are visible)
      const tables = document.querySelectorAll('.tech-table');
      console.log('📊 Total tables found:', tables.length);

      tables.forEach((table, idx) => {
        const section = table.closest('[data-section]');
        const sectionName = section?.getAttribute('data-section');
        const computedStyle = window.getComputedStyle(table);
        console.log(`📊 Table ${idx + 1} in section "${sectionName}":`, {
          tableLayout: computedStyle.tableLayout,
          width: computedStyle.width,
          borderCollapse: computedStyle.borderCollapse,
        });

        // Log first row cells
        const firstRow = table.querySelector('tbody tr');
        if (firstRow) {
          const cells = firstRow.querySelectorAll('td');
          console.log(`  → Cells in first row (${cells.length}):`, Array.from(cells).map((cell, i) => ({
            index: i + 1,
            width: window.getComputedStyle(cell).width,
            content: cell.textContent?.substring(0, 15) + '...',
          })));
        }
      });

      console.log('🖨️ Opening print dialog NOW');
      window.print();

      // AFTER print: Restore original classes
      setTimeout(() => {
        sections.forEach((section, idx) => {
          section.className = originalClasses[idx];
          const sectionName = section.getAttribute('data-section');
          console.log(`  RESTORED: Section ${idx} (${sectionName}): "${section.className}"`);
        });
      }, 100);
    }, 200);
  };

  const toggleAllSections = (checked: boolean) => {
    setPrintSections({
      overview: checked,
      poles: checked,
      cables: checked,
      splices: checked,
      passthroughs: checked,
      splitters: checked,
      customerSplitters: checked,
      onus: checked,
    });
  };

  const getSectionClass = (section: string) => {
    const className = `tech-doc-section ${activeSection === section ? 'active' : 'inactive'}`;
    return className;
  };

  // Log CSS info on mount
  useEffect(() => {
    console.log('📋 TechnicalDocumentation mounted');

    // Check if CSS is loaded
    const stylesheets = Array.from(document.styleSheets);
    const techDocCSS = stylesheets.find(sheet => {
      try {
        return sheet.href?.includes('TechnicalDocumentation.css');
      } catch (e) {
        return false;
      }
    });

    if (techDocCSS) {
      console.log('✅ TechnicalDocumentation.css loaded:', techDocCSS.href);
    } else {
      console.warn('⚠️ TechnicalDocumentation.css NOT found in stylesheets!');
    }

    // Log all loaded stylesheets
    console.log('📄 All stylesheets:', stylesheets.map(s => s.href).filter(Boolean));
  }, []);

  return (
    <div className="technical-documentation">
      {/* Header - No Print */}
      <div className="tech-doc-header no-print">
        <div className="tech-doc-title">
          <h1>📋 Technická dokumentácia</h1>
          <p>Kompletná technická dokumentácia pre technikov</p>
        </div>
        <div className="header-actions">
          <button
            className="btn-filter"
            onClick={() => setShowPrintFilter(!showPrintFilter)}
            title="Filter pre tlač"
          >
            🔍 Filter tlače
          </button>
          <button className="btn-print" onClick={handlePrint}>
            🖨️ Tlačiť
          </button>
        </div>
      </div>

      {/* Print Filter Panel */}
      {showPrintFilter && (
        <div className="print-filter-panel no-print">
          <div className="filter-header">
            <h3>⚙️ Nastavenie tlače</h3>
            <p>Vyberte sekcie ktoré chcete vytlačiť:</p>
          </div>

          <div className="filter-actions">
            <button
              className="btn-select-all"
              onClick={() => toggleAllSections(true)}
            >
              ✅ Vybrať všetko
            </button>
            <button
              className="btn-deselect-all"
              onClick={() => toggleAllSections(false)}
            >
              ❌ Zrušiť výber
            </button>
          </div>

          <div className="filter-options">
            <label className="filter-option">
              <input
                type="checkbox"
                checked={printSections.overview}
                onChange={(e) => setPrintSections({ ...printSections, overview: e.target.checked })}
              />
              <span className="option-icon">📊</span>
              <span className="option-label">Prehľad projektu</span>
            </label>

            <label className="filter-option">
              <input
                type="checkbox"
                checked={printSections.poles}
                onChange={(e) => setPrintSections({ ...printSections, poles: e.target.checked })}
              />
              <span className="option-icon">📍</span>
              <span className="option-label">Stĺpiky</span>
            </label>

            <label className="filter-option">
              <input
                type="checkbox"
                checked={printSections.cables}
                onChange={(e) => setPrintSections({ ...printSections, cables: e.target.checked })}
              />
              <span className="option-icon">🔌</span>
              <span className="option-label">Káble</span>
            </label>

            <label className="filter-option">
              <input
                type="checkbox"
                checked={printSections.splices}
                onChange={(e) => setPrintSections({ ...printSections, splices: e.target.checked })}
              />
              <span className="option-icon">🔗</span>
              <span className="option-label">Zvary</span>
            </label>

            <label className="filter-option">
              <input
                type="checkbox"
                checked={printSections.passthroughs}
                onChange={(e) => setPrintSections({ ...printSections, passthroughs: e.target.checked })}
              />
              <span className="option-icon">🔄</span>
              <span className="option-label">Passthroughs (priechodky)</span>
            </label>

            <label className="filter-option">
              <input
                type="checkbox"
                checked={printSections.splitters}
                onChange={(e) => setPrintSections({ ...printSections, splitters: e.target.checked })}
              />
              <span className="option-icon">🔀</span>
              <span className="option-label">Splittre (nadradené)</span>
            </label>

            <label className="filter-option">
              <input
                type="checkbox"
                checked={printSections.customerSplitters}
                onChange={(e) => setPrintSections({ ...printSections, customerSplitters: e.target.checked })}
              />
              <span className="option-icon">👤</span>
              <span className="option-label">Zákaznícke splittre</span>
            </label>

            <label className="filter-option">
              <input
                type="checkbox"
                checked={printSections.onus}
                onChange={(e) => setPrintSections({ ...printSections, onus: e.target.checked })}
              />
              <span className="option-icon">📡</span>
              <span className="option-label">ONU (zákaznícke pripojenia)</span>
            </label>
          </div>

          <div className="filter-summary">
            <strong>Vybrané sekcie:</strong> {Object.values(printSections).filter(Boolean).length} / 8
          </div>
        </div>
      )}

      {/* Navigation - No Print */}
      <div className="tech-doc-nav no-print">
        <button
          className={activeSection === 'overview' ? 'active' : ''}
          onClick={() => setActiveSection('overview')}
        >
          📊 Prehľad
        </button>
        <button
          className={activeSection === 'poles' ? 'active' : ''}
          onClick={() => setActiveSection('poles')}
        >
          📍 Stĺpiky
        </button>
        <button
          className={activeSection === 'cables' ? 'active' : ''}
          onClick={() => setActiveSection('cables')}
        >
          🔌 Káble
        </button>
        <button
          className={activeSection === 'splices' ? 'active' : ''}
          onClick={() => setActiveSection('splices')}
        >
          🔗 Zvary
        </button>
        <button
          className={activeSection === 'passthroughs' ? 'active' : ''}
          onClick={() => setActiveSection('passthroughs')}
        >
          🔄 Passthroughs
        </button>
        <button
          className={activeSection === 'splitters' ? 'active' : ''}
          onClick={() => setActiveSection('splitters')}
        >
          🔀 Splittre
        </button>
        <button
          className={activeSection === 'customerSplitters' ? 'active' : ''}
          onClick={() => setActiveSection('customerSplitters')}
        >
          👤 Zák. splittre
        </button>
        <button
          className={activeSection === 'onus' ? 'active' : ''}
          onClick={() => setActiveSection('onus')}
        >
          📡 ONU
        </button>
      </div>

      {/* Print Header */}
      <div className="print-only tech-doc-print-header">
        <h1>TECHNICKÁ DOKUMENTÁCIA</h1>
        <div className="print-meta">
          <div>Projekt: <strong>{currentProject?.name}</strong></div>
          <div>Dátum: <strong>{new Date().toLocaleDateString('sk-SK')}</strong></div>
        </div>
      </div>

      {/* Content */}
      <div className="tech-doc-content">
        {/* Overview Section */}
        <section className={getSectionClass('overview')} data-section="overview">
            <h2>📊 PREHĽAD PROJEKTU</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📍</div>
                <div className="stat-info">
                  <div className="stat-value">{totalPoles}</div>
                  <div className="stat-label">Stĺpiky</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🔌</div>
                <div className="stat-info">
                  <div className="stat-value">{totalCables}</div>
                  <div className="stat-label">Káble</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📏</div>
                <div className="stat-info">
                  <div className="stat-value">{totalLength.toFixed(0)}m</div>
                  <div className="stat-label">Celková dĺžka</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🔗</div>
                <div className="stat-info">
                  <div className="stat-value">{totalSplices}</div>
                  <div className="stat-label">Zvary</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🔄</div>
                <div className="stat-info">
                  <div className="stat-value">{totalPassthroughs}</div>
                  <div className="stat-label">Passthroughs</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🔀</div>
                <div className="stat-info">
                  <div className="stat-value">{totalSplitters}</div>
                  <div className="stat-label">Splittre</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📡</div>
                <div className="stat-info">
                  <div className="stat-value">{totalONUs}</div>
                  <div className="stat-label">ONU</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <div className="stat-value">{activeONUs}</div>
                  <div className="stat-label">Aktívne ONU</div>
                </div>
              </div>
            </div>
        </section>

        {/* Poles Section */}
        <section className={getSectionClass('poles')} data-section="poles">
            <h2>📍 ZOZNAM STĹPIKOV ({totalPoles})</h2>
            <table className="tech-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Názov</th>
                  <th>Typ</th>
                  <th>GPS</th>
                  <th>Kazeta</th>
                  <th>Splittre</th>
                  <th>Zvary</th>
                  <th>Priechod</th>
                </tr>
              </thead>
              <tbody>
                {sortedPoles.map((pole, idx) => (
                  <tr key={pole.id}>
                    <td>{idx + 1}</td>
                    <td><strong>{pole.name}</strong></td>
                    <td>{pole.pole_type}</td>
                    <td className="gps-cell">
                      {pole.latitude ? (typeof pole.latitude === 'number' ? pole.latitude.toFixed(6) : pole.latitude) : '-'}, {pole.longitude ? (typeof pole.longitude === 'number' ? pole.longitude.toFixed(6) : pole.longitude) : '-'}
                    </td>
                    <td>{pole.cassette?.has_cassette ? '✅' : '❌'}</td>
                    <td>{pole.cassette?.splitters?.length || 0}</td>
                    <td>{pole.cassette?.splices?.length || 0}</td>
                    <td>{pole.cassette?.pass_through_cables?.length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
        </section>

        {/* Cables Section */}
        <section className={getSectionClass('cables')} data-section="cables">
            <h2>🔌 ZOZNAM KÁBLOV ({totalCables})</h2>
            <table className="tech-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Kábel ID</th>
                  <th>Od</th>
                  <th>Do</th>
                  <th>Typ</th>
                  <th>Dĺžka (m)</th>
                  <th>Útlm (dB)</th>
                </tr>
              </thead>
              <tbody>
                {cables.map((cable, idx) => {
                  const length = cable.length_meters
                    ? (typeof cable.length_meters === 'number' ? cable.length_meters : parseFloat(String(cable.length_meters)))
                    : 0;
                  const loss = length * 0.00035; // 0.35 dB/km
                  return (
                    <tr key={cable.id}>
                      <td>{idx + 1}</td>
                      <td><strong>#{cable.id}</strong></td>
                      <td>{getPoleName(cable.from_pole_id)}</td>
                      <td>{getPoleName(cable.to_pole_id)}</td>
                      <td>{cable.cable_type}</td>
                      <td>{cable.length_meters ? length.toFixed(2) : '-'}</td>
                      <td>{loss.toFixed(3)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        </section>

        {/* Splices Section */}
        <section className={getSectionClass('splices')} data-section="splices">
            <h2>🔗 ZOZNAM ZVAROV ({totalSplices})</h2>
            <table className="tech-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Stĺpik</th>
                  <th>Z (Zdroj)</th>
                  <th>Vlákno</th>
                  <th>Farba</th>
                  <th>DO (Kábel)</th>
                  <th>Vlákno</th>
                  <th>Farba</th>
                  <th>Útlm (dB)</th>
                </tr>
              </thead>
              <tbody>
                {sortedPolesWithCassettes.map((pole) =>
                  pole.cassette?.splices?.map((splice: any, idx: number) => (
                    <tr key={`${pole.id}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td><strong>{pole.name}</strong></td>
                      <td>
                        {splice.from_source_type === 'olt_port'
                          ? `OLT Port ${splice.from_olt_port}`
                          : getCableName(splice.from_cable_id)}
                      </td>
                      <td>{splice.from_fiber || '-'}</td>
                      <td>{splice.from_fiber ? getFiberColorName(splice.from_fiber) : '-'}</td>
                      <td>{getCableName(splice.to_cable_id)}</td>
                      <td>{splice.to_fiber || '-'}</td>
                      <td>{splice.to_fiber ? getFiberColorName(splice.to_fiber) : '-'}</td>
                      <td>{splice.loss_db?.toFixed(3) || '0.030'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
        </section>

        {/* Passthroughs Section */}
        <section className={getSectionClass('passthroughs')} data-section="passthroughs">
            <h2>🔄 ZOZNAM PASSTHROUGHS ({totalPassthroughs})</h2>
            {sortedPolesWithCassettes.map((pole) =>
              pole.cassette?.pass_through_cables?.map((passthrough: any, ptIdx: number) => (
                <div key={`${pole.id}-${ptIdx}`} className="passthrough-detail" style={{ marginBottom: '32px' }}>
                  <h3>
                    Passthrough #{ptIdx + 1} - {pole.name}
                  </h3>
                  <p style={{ marginBottom: '16px', color: '#666' }}>
                    <strong>Kábel:</strong> {getCableName(passthrough.from_cable_id)} → {getCableName(passthrough.to_cable_id)}
                  </p>

                  <table className="tech-table">
                    <thead>
                      <tr>
                        <th>Vlákno č.</th>
                        <th>Farba</th>
                        <th>Stav</th>
                        <th>Odpích do kábla</th>
                        <th>Odpích do vlákna</th>
                        <th>Farba odpíchu</th>
                        <th>Útlm (dB)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {passthrough.fibers?.map((fiber: any) => (
                        <tr key={fiber.fiber_number}>
                          <td><strong>{fiber.fiber_number}</strong></td>
                          <td>{getFiberColorName(fiber.fiber_number)}</td>
                          <td>
                            {fiber.is_tapped ? (
                              <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>🔴 Odpích</span>
                            ) : (
                              <span style={{ color: '#27ae60' }}>✅ Priechod</span>
                            )}
                          </td>
                          <td>
                            {fiber.is_tapped && fiber.tap_to_cable_id
                              ? getCableName(fiber.tap_to_cable_id)
                              : '-'}
                          </td>
                          <td>
                            {fiber.is_tapped && fiber.tap_to_fiber
                              ? fiber.tap_to_fiber
                              : '-'}
                          </td>
                          <td>
                            {fiber.is_tapped && fiber.tap_to_fiber
                              ? getFiberColorName(fiber.tap_to_fiber)
                              : '-'}
                          </td>
                          <td>
                            {fiber.is_tapped && fiber.tap_loss_db
                              ? fiber.tap_loss_db.toFixed(3)
                              : '0.010'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))
            )}
        </section>

        {/* Splitters Section - Non-Customer */}
        <section className={getSectionClass('splitters')} data-section="splitters">
            <h2>🔀 ZOZNAM SPLITTEROV - NADRADENÉ ({sortedPolesWithCassettes.reduce((count, pole) => count + (pole.cassette?.splitters?.filter((s: any) => !s.is_customer_splitter).length || 0), 0)})</h2>
            {sortedPolesWithCassettes.map((pole) =>
              pole.cassette?.splitters?.filter((s: any) => !s.is_customer_splitter).map((splitter: any, idx: number) => {
                // Calculate input power for this splitter
                let inputPower = 0;
                if (splitter.input_source_type === 'olt_port') {
                  const portConfig = pole.cassette?.olt_port_configs?.find(
                    (pc: any) => pc.port_number === splitter.input_olt_port
                  );
                  inputPower = portConfig?.power_dbm || 3.0;
                } else if (splitter.input_source_type === 'splitter_output') {
                  // For cascaded splitters, we'd need to trace back - for now use default
                  inputPower = 0; // Would need full trace
                } else {
                  // Cable input - would need to trace back
                  inputPower = 0; // Would need full trace
                }

                // Calculate output power (input - splitter loss)
                const splitterLoss = SPLITTER_LOSS[splitter.type] || 0;
                const outputPower = inputPower - splitterLoss;

                // Determine if customer splitter
                const isCustomerSplitter = splitter.is_customer_splitter === true;

                return (
                  <div key={`${pole.id}-${idx}`} className="splitter-detail">
                    <h3>
                      Splitter č.{idx + 1} - {pole.name} - Typ: {splitter.type} (Útlm: {SPLITTER_LOSS[splitter.type]} dB)
                      {isCustomerSplitter ? (
                        <span style={{ marginLeft: '16px', color: '#e74c3c', fontWeight: 'bold' }}>
                          👤 ZÁKAZNÍCKY
                        </span>
                      ) : (
                        <span style={{ marginLeft: '16px', color: '#3498db', fontWeight: 'bold' }}>
                          🏢 NADRADENÝ
                        </span>
                      )}
                    </h3>

                    <table className="tech-table">
                      <thead>
                        <tr>
                          <th colSpan={5} className="table-section-header">VSTUP</th>
                        </tr>
                        <tr>
                          <th>Typ zdroja</th>
                          <th>Kábel/Port</th>
                          <th>Vlákno č.</th>
                          <th>Farba</th>
                          <th>Výkon (dBm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            {splitter.input_source_type === 'olt_port'
                              ? 'OLT Port'
                              : splitter.input_source_type === 'splitter_output'
                              ? 'Výstup splittera'
                              : 'Kábel'}
                          </td>
                          <td>
                            {splitter.input_source_type === 'olt_port'
                              ? `Port ${splitter.input_olt_port}`
                              : splitter.input_source_type === 'splitter_output'
                              ? `Splitter #${(splitter.input_splitter_index || 0) + 1} Výstup ${(splitter.input_splitter_output || 0) + 1}`
                              : getCableName(splitter.input_cable_id)}
                          </td>
                          <td>{splitter.input_fiber || '-'}</td>
                          <td>{splitter.input_fiber ? getFiberColorName(splitter.input_fiber) : '-'}</td>
                          <td>
                            <strong style={{ color: inputPower > 0 ? '#27ae60' : '#95a5a6' }}>
                              {inputPower > 0 ? `${inputPower.toFixed(1)} dBm` : 'N/A'}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <table className="tech-table" style={{ marginTop: '16px' }}>
                      <thead>
                        <tr>
                          <th colSpan={7} className="table-section-header">VÝSTUPY</th>
                        </tr>
                        <tr>
                          <th>Výstup č.</th>
                          <th>Kábel</th>
                          <th>Vlákno č.</th>
                          <th>Farba</th>
                          <th>Reverzné</th>
                          <th>Stav</th>
                          <th>Výkon (dBm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {splitter.outputs?.map((output: any, outIdx: number) => {
                          const isReverse = output.cable_id && cables.find((c: any) =>
                            c.id === output.cable_id && c.to_pole_id === pole.id
                          );
                          return (
                            <tr key={outIdx}>
                              <td><strong>{outIdx + 1}</strong></td>
                              <td>{output.cable_id ? getCableName(output.cable_id) : <em>Nepripojené</em>}</td>
                              <td>{output.fiber || '-'}</td>
                              <td>{output.fiber ? getFiberColorName(output.fiber) : '-'}</td>
                              <td>{isReverse ? '⬅️ ÁNO' : output.cable_id ? '→ NIE' : '-'}</td>
                              <td>{output.cable_id ? '✓ Pripojené' : '○ Voľné'}</td>
                              <td>
                                <strong style={{ color: outputPower > 0 ? '#27ae60' : '#95a5a6' }}>
                                  {outputPower > 0 ? `${outputPower.toFixed(1)} dBm` : 'N/A'}
                                </strong>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })
            )}
        </section>

        {/* Customer Splitters Section */}
        <section className={getSectionClass('customerSplitters')} data-section="customerSplitters">
            <h2>👤 ZOZNAM SPLITTEROV - ZÁKAZNÍCKE ({sortedPolesWithCassettes.reduce((count, pole) => count + (pole.cassette?.splitters?.filter((s: any) => s.is_customer_splitter).length || 0), 0)})</h2>
            {sortedPolesWithCassettes.map((pole) =>
              pole.cassette?.splitters?.filter((s: any) => s.is_customer_splitter).map((splitter: any, idx: number) => {
                // Calculate input power for this splitter
                let inputPower = 0;
                if (splitter.input_source_type === 'olt_port') {
                  const portConfig = pole.cassette?.olt_port_configs?.find(
                    (pc: any) => pc.port_number === splitter.input_olt_port
                  );
                  inputPower = portConfig?.power_dbm || 3.0;
                } else if (splitter.input_source_type === 'splitter_output') {
                  // For cascaded splitters, we'd need to trace back - for now use default
                  inputPower = 0; // Would need full trace
                } else {
                  // Cable input - would need to trace back
                  inputPower = 0; // Would need full trace
                }

                // Calculate output power (input - splitter loss)
                const splitterLoss = SPLITTER_LOSS[splitter.type] || 0;
                const outputPower = inputPower - splitterLoss;

                // Determine if customer splitter
                const isCustomerSplitter = splitter.is_customer_splitter === true;

                return (
                  <div key={`${pole.id}-${idx}`} className="splitter-detail">
                    <h3>
                      Splitter č.{idx + 1} - {pole.name} - Typ: {splitter.type} (Útlm: {SPLITTER_LOSS[splitter.type]} dB)
                      {isCustomerSplitter ? (
                        <span style={{ marginLeft: '16px', color: '#e74c3c', fontWeight: 'bold' }}>
                          👤 ZÁKAZNÍCKY
                        </span>
                      ) : (
                        <span style={{ marginLeft: '16px', color: '#3498db', fontWeight: 'bold' }}>
                          🏢 NADRADENÝ
                        </span>
                      )}
                    </h3>

                    <table className="tech-table">
                      <thead>
                        <tr>
                          <th colSpan={5} className="table-section-header">VSTUP</th>
                        </tr>
                        <tr>
                          <th>Typ zdroja</th>
                          <th>Kábel/Port</th>
                          <th>Vlákno č.</th>
                          <th>Farba</th>
                          <th>Výkon (dBm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            {splitter.input_source_type === 'olt_port'
                              ? 'OLT Port'
                              : splitter.input_source_type === 'splitter_output'
                              ? 'Výstup splittera'
                              : 'Kábel'}
                          </td>
                          <td>
                            {splitter.input_source_type === 'olt_port'
                              ? `Port ${splitter.input_olt_port}`
                              : splitter.input_source_type === 'splitter_output'
                              ? `Splitter #${(splitter.input_splitter_index || 0) + 1} Výstup ${(splitter.input_splitter_output || 0) + 1}`
                              : getCableName(splitter.input_cable_id)}
                          </td>
                          <td>{splitter.input_fiber || '-'}</td>
                          <td>{splitter.input_fiber ? getFiberColorName(splitter.input_fiber) : '-'}</td>
                          <td>
                            <strong style={{ color: inputPower > 0 ? '#27ae60' : '#95a5a6' }}>
                              {inputPower > 0 ? `${inputPower.toFixed(1)} dBm` : 'N/A'}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <table className="tech-table" style={{ marginTop: '16px' }}>
                      <thead>
                        <tr>
                          <th colSpan={7} className="table-section-header">VÝSTUPY</th>
                        </tr>
                        <tr>
                          <th>Výstup č.</th>
                          <th>Kábel</th>
                          <th>Vlákno č.</th>
                          <th>Farba</th>
                          <th>ONU</th>
                          <th>Výkon (dBm)</th>
                          <th>Stav</th>
                        </tr>
                      </thead>
                      <tbody>
                        {splitter.outputs?.map((output: any, outIdx: number) => (
                          <tr key={outIdx}>
                            <td><strong>{outIdx + 1}</strong></td>
                            <td>{output.cable_id ? getCableName(output.cable_id) : '-'}</td>
                            <td>{output.fiber_number || '-'}</td>
                            <td>{output.fiber_number ? getFiberColorName(output.fiber_number) : '-'}</td>
                            <td>{output.onu_name || '-'}</td>
                            <td>
                              <strong style={{ color: outputPower > 0 ? '#27ae60' : '#95a5a6' }}>
                                {outputPower > 0 ? `${outputPower.toFixed(1)} dBm` : 'N/A'}
                              </strong>
                            </td>
                            <td>
                              {output.onu_name ? (
                                <span style={{ color: '#27ae60' }}>✅ Pripojené</span>
                              ) : (
                                <span style={{ color: '#95a5a6' }}>⚪ Voľné</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })
            )}
        </section>

        {/* ONU Section */}
        <section className={getSectionClass('onus')} data-section="onus">
            <h2>📡 ZOZNAM ONU ({totalONUs})</h2>
            <table className="tech-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Názov</th>
                  <th>Zákazník</th>
                  <th>Adresa</th>
                  <th>Stĺpik</th>
                  <th>Splitter</th>
                  <th>Port</th>
                  <th>Vlákno</th>
                  <th>Signál (dBm)</th>
                  <th>Stav</th>
                </tr>
              </thead>
              <tbody>
                {onus.map((onu, idx) => {
                  const signal = onu.signal_strength
                    ? (typeof onu.signal_strength === 'string' ? parseFloat(onu.signal_strength) : onu.signal_strength)
                    : null;
                  const status = signal && signal > -28 ? '✅ OK' : signal ? '⚠️ Slabý' : '❌ Offline';
                  return (
                    <tr key={onu.id}>
                      <td>{idx + 1}</td>
                      <td><strong>{onu.name}</strong></td>
                      <td>{onu.customer_name || '-'}</td>
                      <td>{onu.notes || '-'}</td>
                      <td>{getPoleName(onu.pole_id)}</td>
                      <td>{onu.splitter_index !== undefined ? `#${onu.splitter_index + 1}` : '-'}</td>
                      <td>{onu.splitter_port || '-'}</td>
                      <td>{onu.fiber_color || '-'}</td>
                      <td>{onu.signal_strength || '-'}</td>
                      <td>{status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        </section>
      </div>
    </div>
  );
};
